import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { useToast } from "../../hooks/use-toast";
import { LogOut, Save, Upload, Trash2, ExternalLink, FileText, Eye, Plus, X } from "lucide-react";
import {
  fetchContent,
  updateContent,
  uploadFile,
  fileUrl,
  verifyToken,
  logout,
} from "../../lib/api";
import AdminProjects from "./AdminProjects";
import { SkillsEditor, ExperienceEditor, EducationEditor, StatsEditor } from "./AdminLists";
import VideosEditor from "./VideosEditor";
import AdminCopy from "./AdminCopy";

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[11px] tracking-[0.22em] uppercase text-[#1A1714]/60 mb-2">{label}</label>
    {children}
  </div>
);

const inputClass =
  "bg-[#F6F1E8] border border-[#1A1714]/15 rounded-sm focus-visible:ring-1 focus-visible:ring-[#1A1714]";

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingPortrait, setUploadingPortrait] = useState(false);
  const [uploadingCV, setUploadingCV] = useState(false);

  useEffect(() => {
    (async () => {
      const ok = await verifyToken();
      if (!ok) {
        navigate("/admin", { replace: true });
        return;
      }
      const c = await fetchContent();
      setContent(c);
    })();
  }, [navigate]);

  if (!content) return <div className="min-h-screen" />;

  const setProfile = (patch) =>
    setContent((c) => ({ ...c, profile: { ...c.profile, ...patch } }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateContent({
        profile: content.profile,
        stats: content.stats,
        skills: content.skills,
        experience: content.experience,
        education: content.education,
        showreel: content.showreel || [],
        copy: content.copy || {},
        cv: content.cv,
      });
      setContent(updated);
      toast({ title: "Saved", description: "Your portfolio has been updated." });
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const onPortraitUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingPortrait(true);
    try {
      const res = await uploadFile(f);
      setProfile({ portrait: res.url });
      toast({ title: "Portrait uploaded", description: "Don't forget to click Save." });
    } catch (err) {
      toast({ title: "Upload failed", description: err?.response?.data?.detail || err.message, variant: "destructive" });
    } finally {
      setUploadingPortrait(false);
      e.target.value = "";
    }
  };

  const onCVUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingCV(true);
    try {
      const res = await uploadFile(f);
      setContent((c) => ({ ...c, cv: { url: res.url, filename: res.filename } }));
      toast({ title: "CV uploaded", description: "Click Save to publish." });
    } catch (err) {
      toast({ title: "Upload failed", description: err?.response?.data?.detail || err.message, variant: "destructive" });
    } finally {
      setUploadingCV(false);
      e.target.value = "";
    }
  };

  const removeCV = () => setContent((c) => ({ ...c, cv: null }));

  return (
    <div className="min-h-screen bg-[#F6F1E8] pb-24">
      <header className="sticky top-0 z-20 backdrop-blur bg-[#F6F1E8]/85 border-b border-[#1A1714]/12">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-serif-display text-xl">
              Tatenda<span className="text-[#B5523A]">.</span>
            </span>
            <span className="text-[11px] tracking-[0.22em] uppercase text-[#1A1714]/55 border-l border-[#1A1714]/20 pl-3">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="hidden sm:inline-flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase border border-[#1A1714]/25 px-3 py-2 rounded-sm hover:bg-[#1A1714] hover:text-[#F6F1E8] transition-colors"
            >
              <Eye size={14} /> View site
            </a>
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-[#1A1714] text-[#F6F1E8] px-4 py-2 rounded-sm text-[12px] tracking-[0.18em] uppercase hover:bg-[#2c2620] disabled:opacity-60 transition-colors"
            >
              <Save size={14} /> {saving ? "Saving…" : "Save changes"}
            </button>
            <button
              onClick={() => { logout(); navigate("/admin"); }}
              className="inline-flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase text-[#1A1714]/60 hover:text-[#1A1714]"
              title="Logout"
            >
              <LogOut size={14} /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 md:px-10 pt-10">
        <h1 className="font-serif-display text-4xl md:text-5xl tracking-[-0.01em]">Edit portfolio</h1>
        <p className="mt-2 text-[#1A1714]/65">All changes are saved when you click “Save changes”.</p>

        <Tabs defaultValue="profile" className="mt-10">
          <TabsList className="bg-[#EFE8DA] flex flex-wrap h-auto p-1 gap-1">
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="copy">Site Copy</TabsTrigger>
            <TabsTrigger value="stats">Stats</TabsTrigger>
            <TabsTrigger value="skills">Skills</TabsTrigger>
            <TabsTrigger value="experience">Experience</TabsTrigger>
            <TabsTrigger value="education">Education</TabsTrigger>
            <TabsTrigger value="projects">Projects</TabsTrigger>
            <TabsTrigger value="showreel">Showreel</TabsTrigger>
            <TabsTrigger value="cv">CV</TabsTrigger>
          </TabsList>

          {/* Profile */}
          <TabsContent value="profile" className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="md:col-span-1">
                <Field label="Portrait photo">
                  <div className="aspect-[4/5] bg-[#EFE8DA] border border-[#1A1714]/15 rounded-sm overflow-hidden">
                    {content.profile.portrait && (
                      <img src={fileUrl(content.profile.portrait)} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <label className="mt-3 inline-flex items-center gap-2 cursor-pointer text-[12px] tracking-[0.18em] uppercase border border-[#1A1714]/25 px-3 py-2 rounded-sm hover:bg-[#1A1714] hover:text-[#F6F1E8] transition-colors">
                    <Upload size={14} /> {uploadingPortrait ? "Uploading…" : "Replace photo"}
                    <input type="file" accept="image/*" onChange={onPortraitUpload} className="hidden" />
                  </label>
                </Field>
              </div>
              <div className="md:col-span-2 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="First name"><Input className={inputClass} value={content.profile.firstName || ""} onChange={(e) => setProfile({ firstName: e.target.value })} /></Field>
                  <Field label="Role / title"><Input className={inputClass} value={content.profile.role || ""} onChange={(e) => setProfile({ role: e.target.value })} /></Field>
                </div>
                <Field label="Tagline"><Input className={inputClass} value={content.profile.tagline || ""} onChange={(e) => setProfile({ tagline: e.target.value })} /></Field>
                <Field label="About / summary"><Textarea rows={6} className={inputClass} value={content.profile.summary || ""} onChange={(e) => setProfile({ summary: e.target.value })} /></Field>
                <Field label="Location"><Input className={inputClass} value={content.profile.location || ""} onChange={(e) => setProfile({ location: e.target.value })} /></Field>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <Field label="Email"><Input className={inputClass} value={content.profile.email || ""} onChange={(e) => setProfile({ email: e.target.value })} /></Field>
                  <Field label="Phone"><Input className={inputClass} value={content.profile.phone || ""} onChange={(e) => setProfile({ phone: e.target.value })} /></Field>
                  <Field label="LinkedIn URL"><Input className={inputClass} value={content.profile.linkedin || ""} onChange={(e) => setProfile({ linkedin: e.target.value })} /></Field>
                  <Field label="Instagram URL"><Input className={inputClass} value={content.profile.instagram || ""} onChange={(e) => setProfile({ instagram: e.target.value })} /></Field>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="stats" className="mt-8">
            <StatsEditor stats={content.stats || []} onChange={(stats) => setContent((c) => ({ ...c, stats }))} />
          </TabsContent>

          <TabsContent value="copy" className="mt-8">
            <AdminCopy copy={content.copy || {}} onChange={(copy) => setContent((c) => ({ ...c, copy }))} />
          </TabsContent>

          <TabsContent value="skills" className="mt-8">
            <SkillsEditor skills={content.skills || []} onChange={(skills) => setContent((c) => ({ ...c, skills }))} />
          </TabsContent>

          <TabsContent value="experience" className="mt-8">
            <ExperienceEditor experience={content.experience || []} onChange={(experience) => setContent((c) => ({ ...c, experience }))} />
          </TabsContent>

          <TabsContent value="education" className="mt-8">
            <EducationEditor education={content.education || []} onChange={(education) => setContent((c) => ({ ...c, education }))} />
          </TabsContent>

          <TabsContent value="projects" className="mt-8">
            <AdminProjects />
          </TabsContent>

          <TabsContent value="showreel" className="mt-8">
            <div className="max-w-4xl">
              <h2 className="font-serif-display text-2xl mb-2">Showreel</h2>
              <p className="text-[#1A1714]/65 text-sm">
                Optional dedicated section on the home page showing campaign reels and video work.
                Mix uploaded videos with YouTube / Vimeo / TikTok links.
              </p>
              <div className="mt-6">
                <VideosEditor
                  videos={content.showreel || []}
                  onChange={(showreel) => setContent((c) => ({ ...c, showreel }))}
                  label="Showreel videos"
                  helper="Visitors see these in the dark Showreel section between Work and Skills."
                />
              </div>
              <p className="mt-4 text-xs text-[#1A1714]/50">Click "Save changes" above to publish.</p>
            </div>
          </TabsContent>

          <TabsContent value="cv" className="mt-8">
            <div className="max-w-2xl">
              <h2 className="font-serif-display text-2xl mb-2">Your CV / Resume</h2>
              <p className="text-[#1A1714]/65 text-sm">PDF or DOCX. Up to 10 MB. Visitors see a “Download CV” button on the hero.</p>
              <div className="mt-6 bg-[#EFE8DA] border border-[#1A1714]/15 rounded-sm p-6">
                {content.cv?.url ? (
                  <div className="flex flex-wrap items-center gap-4">
                    <FileText className="text-[#B5523A]" />
                    <div className="flex-1 min-w-0">
                      <div className="font-serif-display text-lg truncate">{content.cv.filename}</div>
                      <a href={fileUrl(content.cv.url)} target="_blank" rel="noreferrer" className="text-[12px] tracking-[0.18em] uppercase text-[#1A1714]/60 hover:text-[#1A1714] inline-flex items-center gap-1">
                        Open <ExternalLink size={12} />
                      </a>
                    </div>
                    <button onClick={removeCV} className="inline-flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase text-[#1A1714]/60 hover:text-[#B5523A]">
                      <Trash2 size={14} /> Remove
                    </button>
                  </div>
                ) : (
                  <div className="text-[#1A1714]/55 text-sm">No CV uploaded yet.</div>
                )}
                <label className="mt-5 inline-flex items-center gap-2 cursor-pointer bg-[#1A1714] text-[#F6F1E8] px-4 py-2 rounded-sm text-[12px] tracking-[0.18em] uppercase hover:bg-[#2c2620]">
                  <Upload size={14} /> {uploadingCV ? "Uploading…" : (content.cv?.url ? "Replace CV" : "Upload CV")}
                  <input type="file" accept=".pdf,.doc,.docx" onChange={onCVUpload} className="hidden" />
                </label>
              </div>
              <p className="mt-3 text-xs text-[#1A1714]/50">Remember to click “Save changes” above for the hero button to update.</p>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
